import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X as XIcon, Loader2, Trash2, Fuel, Plus } from 'lucide-react';
import { fmtDay, ymd, errMsg } from '../porters/porterUi';
import { money } from './StaffProfile';

const CAT_CLS = { fuel: 'bg-orange-100 text-orange-800', tip: 'bg-emerald-100 text-emerald-800', tolls: 'bg-sky-100 text-sky-800', parking: 'bg-purple-100 text-purple-800', other: 'bg-slate-100 text-slate-700' };
const FIELD = 'h-10 rounded-md border border-slate-200 bg-white px-2 text-sm';

const AddForm = ({ authFetch, categories, vans, onAdded, defaultDate }) => {
  const [f, setF] = useState({ date: defaultDate, category: 'fuel', amount: '', quote_id: '', van_id: '', note: '' });
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => {
    let live = true;
    authFetch('get', `/admin/day-sheet?date=${f.date}`).then(({ data }) => { if (live) setJobs(data.jobs); }).catch(() => setJobs([]));
    return () => { live = false; };
  }, [authFetch, f.date]);
  const submit = async (e) => {
    e.preventDefault();
    if (!(parseFloat(f.amount) > 0)) { toast.error('Enter the amount.'); return; }
    setBusy(true);
    try {
      await authFetch('post', '/admin/daily-costs', { ...f, amount: parseFloat(f.amount), quote_id: f.quote_id || null, van_id: f.van_id || null });
      toast.success(`${categories[f.category]} ${money(parseFloat(f.amount))} added for ${fmtDay(f.date)}.`);
      setF(p => ({ ...p, amount: '', note: '', quote_id: '' }));
      onAdded();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="dc-form">
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(categories).map(([k, l]) => (
          <button key={k} type="button" onClick={() => set('category', k)} className={`h-9 px-3 rounded-full text-xs font-bold border ${f.category === k ? 'bg-[color:var(--mv-navy)] text-white border-transparent' : 'bg-white border-slate-200 text-slate-600'}`} data-testid={`dc-cat-${k}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input type="date" value={f.date} max={ymd(new Date())} onChange={(e) => e.target.value && set('date', e.target.value)} className={FIELD} data-testid="dc-date" />
        <div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">£</span>
          <input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => set('amount', e.target.value)} placeholder="Amount" className={`${FIELD} w-full pl-5`} data-testid="dc-amount" /></div>
        <select value={f.van_id} onChange={(e) => set('van_id', e.target.value)} className={FIELD} data-testid="dc-van">
          <option value="">Van (optional)</option>
          {vans.map(v => <option key={v.id} value={v.id}>{v.name || v.reg}</option>)}
        </select>
        <select value={f.quote_id} onChange={(e) => set('quote_id', e.target.value)} className={FIELD} data-testid="dc-job">
          <option value="">Job (optional)</option>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.time} #{j.ref} {j.customer}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <input value={f.note} onChange={(e) => set('note', e.target.value)} maxLength={200} placeholder="Note, e.g. Shell A13 or Rainham tip 1.2t" className={`${FIELD} flex-1`} data-testid="dc-note" />
        <button type="submit" disabled={busy} className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50" data-testid="dc-add">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Add</button>
      </div>
    </form>
  );
};

export const DailyCostsModal = ({ authFetch, period, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [vans, setVans] = useState([]);
  const load = useCallback(() => authFetch('get', `/admin/daily-costs?start=${period.start}&end=${period.end}`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [authFetch, period.start, period.end]);
  useEffect(() => { load(); authFetch('get', '/vans').then(({ data }) => setVans(data.vans)).catch(() => {}); }, [load, authFetch]);
  const changed = () => { load(); onChanged(); };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.category_label} ${money(c.amount)} on ${fmtDay(c.date)}?`)) return;
    try { await authFetch('delete', `/admin/daily-costs/${c.id}`); toast.success('Deleted.'); changed(); } catch (e) { toast.error(errMsg(e)); }
  };
  const today = ymd(new Date());
  const def = today >= period.start && today <= period.end ? today : period.end < today ? period.end : period.start;
  const byDay = (d?.items || []).reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});
  const totals = (d?.items || []).reduce((acc, c) => ({ ...acc, [c.category]: (acc[c.category] || 0) + c.amount }), {});
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="dc-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-xl max-h-[92vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="flex-1 font-display text-xl font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2"><Fuel className="w-5 h-5 text-[color:var(--mv-orange)]" />Daily costs</h3>
          <button type="button" onClick={onClose} className="w-10 h-10 inline-flex items-center justify-center text-slate-500" aria-label="Close" data-testid="dc-close"><XIcon className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-500">Log fuel, tip fees and other costs as they happen. They come off that day's profit. Nobody's pay is affected.</p>
        {!d ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <>
            <AddForm authFetch={authFetch} categories={d.categories} vans={vans} onAdded={changed} defaultDate={def} />
            <div className="flex flex-wrap gap-2 text-xs" data-testid="dc-totals">
              <span className="font-black text-[color:var(--mv-navy)]">{fmtDay(period.start)} – {fmtDay(period.end)}: {money(d.total)}</span>
              {Object.entries(totals).map(([k, v]) => <span key={k} className={`px-2 py-0.5 rounded-full font-bold ${CAT_CLS[k]}`} data-testid={`dc-total-${k}`}>{d.categories[k]} {money(v)}</span>)}
            </div>
            {d.items.length === 0 && <div className="text-sm text-slate-500" data-testid="dc-empty">No daily costs logged in this period yet.</div>}
            {Object.entries(byDay).map(([day, list]) => (
              <div key={day}>
                <div className="text-[11px] font-black uppercase tracking-wider text-slate-500 mt-2">{fmtDay(day)} · {money(list.reduce((a, b) => a + b.amount, 0))}</div>
                {list.map(c => (
                  <div key={c.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 text-sm" data-testid={`dc-item-${c.id.slice(0, 6)}`}>
                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${CAT_CLS[c.category]}`}>{c.category_label}</span>
                    <span className="flex-1 min-w-0 truncate text-slate-600">{[c.van, c.job_ref && `#${c.job_ref}`, c.note].filter(Boolean).join(' · ') || '—'}</span>
                    <strong>{money(c.amount)}</strong>
                    <button type="button" onClick={() => del(c)} className="w-9 h-9 rounded-md text-red-600 hover:bg-red-50 inline-flex items-center justify-center" aria-label="Delete" data-testid={`dc-delete-${c.id.slice(0, 6)}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
